import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaArrowLeft, FaDownload, FaLock } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// M-CHAT questions with critical items marked
const MCHAT_QUESTIONS = [
  { id: 1, text: "Does your child enjoy being swung, bounced on your knee, etc.?", risk_answer: "no", critical: false },
  { id: 2, text: "Does your child take an interest in other children?", risk_answer: "no", critical: true },
  { id: 3, text: "Does your child like climbing on things, such as stairs?", risk_answer: "no", critical: false },
  { id: 4, text: "Does your child enjoy playing peek-a-boo/hide-and-seek?", risk_answer: "no", critical: false },
  { id: 5, text: "Does your child ever pretend, for example, to talk on the phone or take care of a doll or anything else?", risk_answer: "no", critical: true },
  { id: 6, text: "Does your child ever use his/her index finger to point, to ask for something?", risk_answer: "no", critical: false },
  { id: 7, text: "Does your child ever use his/her index finger to point, to indicate interest in something?", risk_answer: "no", critical: true },
  { id: 8, text: "Can your child play properly with small toys (e.g. cars or bricks) without just mouthing, fiddling, or dropping them?", risk_answer: "no", critical: false },
  { id: 9, text: "Does your child ever bring objects over to you (parent) to show you something?", risk_answer: "no", critical: true },
  { id: 10, text: "Does your child look you in the eye for more than a second or two?", risk_answer: "no", critical: false },
  { id: 11, text: "Does your child ever seem oversensitive to noise? (e.g., plugging ears)", risk_answer: "yes", critical: false },
  { id: 12, text: "Does your child smile in response to your face or your smile?", risk_answer: "no", critical: false },
  { id: 13, text: "Does your child imitate you? (e.g., you make a face-will your child imitate it?)", risk_answer: "no", critical: true },
  { id: 14, text: "Does your child respond to his/her name when you call?", risk_answer: "no", critical: true },
  { id: 15, text: "If you point at a toy across the room, does your child look at it?", risk_answer: "no", critical: true },
  { id: 16, text: "Does your child walk?", risk_answer: "no", critical: false },
  { id: 17, text: "Does your child look at things you are looking at?", risk_answer: "no", critical: false },
  { id: 18, text: "Does your child make unusual finger movements near his/her face?", risk_answer: "yes", critical: false },
  { id: 19, text: "Does your child try to attract your attention to his/her activity?", risk_answer: "no", critical: false },
  { id: 20, text: "Have you ever wondered if your child is deaf?", risk_answer: "yes", critical: false },
];

const AssessmentReport = () => {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [isTempUser, setIsTempUser] = useState(false);
  const { currentUser } = useAuth();

  const isPaymentRequired = () => {
    // Payment is required if:
    // 1. User is logged in AND
    // 2. The assessment doesn't have payment status or payment is not completed
    if (!currentUser) return false; // Guest users see limited view
    return !assessmentData?.payment || assessmentData.payment.status !== 'completed';
  };

  useEffect(() => {
    console.log('AssessmentReport mounted with assessmentId:', assessmentId);
    console.log('Location state:', location.state);
    
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      console.log('Auth state changed, user:', currentUser);
      setUser(currentUser);
      
      // Check if we have assessment data in location state (for temp users)
      if (location.state?.assessmentData) {
        console.log('Using assessment data from location state');
        setAssessmentData(location.state.assessmentData);
        setLoading(false);
        setIsTempUser(true);
      } else {
        console.log('No assessment data in location state');
      }
    });
    
    return () => {
      console.log('AssessmentReport unmounting');
      unsubscribe();
    };
  }, [location.state, assessmentId]);

  useEffect(() => {
    if (!assessmentId) {
      setError('No assessment ID provided.');
      setLoading(false);
      return;
    }

    // If we have temp data, no need to fetch
    if (isTempUser && assessmentData) {
      return;
    }

    const fetchAssessment = async () => {
      try {
        console.log('fetchAssessment called for ID:', assessmentId);
        setLoading(true);
        setError('');
        
        // Get the assessment document directly by ID
        const docRef = doc(db, 'assessments', assessmentId);
        console.log('Fetching document from Firestore...');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          console.log('Document found in Firestore:', docSnap.data());
          const data = { id: docSnap.id, ...docSnap.data() };
          
          // If user is logged in, verify ownership
          if (user && data.userId !== user.uid) {
            console.log('Permission denied: User ID does not match assessment owner');
            setError('You do not have permission to view this report.');
          } else {
            console.log('Setting assessment data');
            setAssessmentData(data);
          }
        } else {
          console.log('Document not found in Firestore, checking for temp assessment');
          // Check if this is a temporary assessment ID
          if (assessmentId.startsWith('temp_')) {
            console.log('Looking for temp assessment in localStorage');
            const pendingAssessments = JSON.parse(localStorage.getItem('pendingAssessments') || '[]');
            console.log('Pending assessments in localStorage:', pendingAssessments);
            const tempAssessment = pendingAssessments.find(a => a.tempId === assessmentId);
            
            if (tempAssessment) {
              console.log('Found temp assessment:', tempAssessment);
              setAssessmentData({
                ...tempAssessment,
                id: tempAssessment.tempId,
                isTemporary: true
              });
              return;
            } else {
              console.log('No matching temp assessment found');
            }
          }
          console.error('Assessment not found in any location');
          setError('Assessment not found. It may have been deleted or you may not have access to it.');
        }
      } catch (err) {
        console.error('Error fetching assessment:', err);
        setError('Failed to load assessment data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we don't have temp data
    if (!isTempUser) {
      fetchAssessment();
    }
  }, [assessmentId, user, isTempUser]);

  // Calculate risk level based on M-CHAT scoring
  const calculateRiskLevel = (data) => {
    if (!data || !data.answers) return 'Unknown';
    
    console.log('Calculating risk level for answers:', data.answers);
    
    const answers = data.answers;
    let failedCount = 0;
    let criticalFails = 0;
    
    MCHAT_QUESTIONS.forEach(question => {
      // Access answer by question ID directly from the answers object
      const answer = answers[question.id];
      console.log(`Question ${question.id} - Answer: ${answer}, Expected: ${question.risk_answer}`);
      
      if (answer === question.risk_answer) {
        failedCount++;
        if (question.critical) {
          criticalFails++;
        }
        console.log(`Question ${question.id} failed (${failedCount} total fails, ${criticalFails} critical)`);
      }
    });
    
    console.log(`Final counts - Total fails: ${failedCount}, Critical fails: ${criticalFails}`);
    
    // M-CHAT scoring rules
    if (failedCount >= 8) return 'High Risk';
    if (failedCount >= 3 || criticalFails >= 2) return 'Medium Risk';
    if (failedCount <= 2) return 'Low Risk';
    
    return 'Unknown';
  };

  const generateReportContent = () => {
    if (!assessmentData) return null;
    
    const riskLevel = calculateRiskLevel(assessmentData);
    const criticalItems = [2, 5, 7, 9, 13, 14, 15];
    
    const doc = new jsPDF();
    
    // Add header with logo and title
    doc.setFontSize(20);
    doc.setTextColor(41, 128, 185);
    doc.setFont('helvetica', 'bold');
    doc.text('M-CHAT-R/F Assessment Report', 105, 25, { align: 'center' });
    
    // Add assessment metadata
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Assessment ID: ${assessmentData.id || 'N/A'}`, 14, 35);
    doc.text(`Generated on: ${new Date(assessmentData.createdAt?.seconds * 1000 || Date.now()).toLocaleString()}`, 105, 35, { align: 'center' });
    doc.text(`Page 1 of 2`, 196, 35, { align: 'right' });
    
    // Add decorative line
    doc.setDrawColor(200);
    doc.line(14, 40, 196, 40);
    
    // Add child information section
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('Child Information', 14, 60);
    doc.setDrawColor(200);
    doc.line(14, 62, 196, 62);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Child's Name: ${assessmentData.childName || 'Not provided'}`, 20, 75);
    doc.text(`Age: ${assessmentData.childsAgeInMonths || 'Not provided'} months`, 20, 85);
    doc.text(`Gender: ${assessmentData.childGender || 'Not provided'}`, 20, 95);
    
    // Add parent/guardian information if available
    if (assessmentData.parentName || assessmentData.mobileNumber) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Parent/Guardian Information', 14, 115);
      doc.line(14, 117, 196, 117);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      let yPos = 130;
      if (assessmentData.parentName) {
        doc.text(`Name: ${assessmentData.parentName}`, 20, yPos);
        yPos += 10;
      }
      if (assessmentData.mobileNumber) {
        doc.text(`Contact: ${assessmentData.mobileNumber}`, 20, yPos);
        yPos += 10;
      }
      if (assessmentData.email) {
        doc.text(`Email: ${assessmentData.email}`, 20, yPos);
        yPos += 10;
      }
    }
    
    // Add risk level section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Assessment Results', 14, 160);
    doc.setDrawColor(200);
    doc.line(14, 162, 196, 162);
    
    // Risk level indicator with colored box
    doc.setFontSize(16);
    const riskText = `Risk Level: ${riskLevel}`;
    const riskWidth = doc.getTextWidth(riskText) + 20;
    const riskX = 105 - (riskWidth / 2);
    
    // Set color based on risk level
    if (riskLevel === 'High Risk') {
      doc.setFillColor(248, 215, 218); // Light red
      doc.setTextColor(114, 28, 36); // Dark red
    } else if (riskLevel === 'Medium Risk') {
      doc.setFillColor(255, 243, 205); // Light yellow
      doc.setTextColor(133, 100, 4); // Dark yellow
    } else {
      doc.setFillColor(212, 237, 218); // Light green
      doc.setTextColor(21, 87, 36); // Dark green
    }
    
    // Draw colored box
    doc.roundedRect(riskX - 10, 175, riskWidth, 20, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text(riskText, 105, 188, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0);
    doc.setFont('helvetica', 'normal');
    
    // Add risk interpretation
    doc.setFontSize(11);
    const interpretation = getRiskInterpretation(riskLevel);
    const splitText = doc.splitTextToSize(interpretation, 180);
    doc.text(splitText, 20, 210);
    
    // Add assessment date
    doc.setFontSize(10);
    doc.setTextColor(100);
    const assessmentDate = assessmentData.createdAt?.toDate ? 
      assessmentData.createdAt.toDate().toLocaleString() : 
      new Date().toLocaleString();
    doc.text(`Assessment completed on: ${assessmentDate}`, 14, 230);
    
    // Add page break for questions
    doc.addPage();
    
    // Add questions header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Assessment Questions & Responses', 14, 20);
    doc.setDrawColor(200);
    doc.line(14, 22, 196, 22);
    
    // Add answers table with improved formatting
    const tableColumn = [
      { header: 'Question', dataKey: 'question' },
      { header: 'Response', dataKey: 'answer' },
      { header: 'Risk', dataKey: 'risk' }
    ];
    
    const tableRows = MCHAT_QUESTIONS.map((q, index) => {
      const answer = assessmentData.answers && assessmentData.answers[q.id] !== undefined 
        ? assessmentData.answers[q.id] === 'yes' ? 'Yes' : 'No'
        : 'Not answered';
      const isRiskAnswer = answer.toLowerCase() === q.risk_answer.toLowerCase();
      
      return {
        question: { content: q.text, styles: { fontStyle: 'normal' } },
        answer: { content: answer, styles: { fontStyle: 'normal' } },
        risk: { 
          content: isRiskAnswer ? 'Yes' : 'No',
          styles: { 
            fontStyle: 'bold',
            textColor: isRiskAnswer ? [231, 76, 60] : [0, 0, 0]
          }
        }
      };
    });
    
    // Add table with custom styling
    autoTable(doc, {
      columns: tableColumn,
      body: tableRows,
      startY: 30,
      margin: { left: 14, right: 14 },
      styles: {
        fontSize: 10,
        cellPadding: 4,
        lineColor: [221, 221, 221],
        lineWidth: 0.5,
        font: 'helvetica',
        textColor: [0, 0, 0],
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headerStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
        lineWidth: 0.5,
        lineColor: [41, 128, 185]
      },
      columnStyles: {
        question: { cellWidth: 120, fontStyle: 'normal' },
        answer: { cellWidth: 30, halign: 'center' },
        risk: { cellWidth: 20, halign: 'center' }
      },
      didParseCell: (data) => {
        // Handle risk column styling
        if (data.column.dataKey === 'risk' && data.cell.raw.content === 'Yes') {
          data.cell.styles.fillColor = [255, 235, 238]; // Light red background for risk items
        }
      },
      willDrawCell: (data) => {
        // Add question numbers
        if (data.column.dataKey === 'question' && data.row.index !== undefined) {
          doc.setFont('helvetica', 'bold');
          doc.text(`Q${data.row.index + 1}. `, data.cell.x + 2, data.cell.y + 8);
          doc.setFont('helvetica', 'normal');
        }
      }
    });
    
    // Add interpretation section on a new page
    doc.addPage();
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Interpretation & Next Steps', 14, 20);
    doc.setDrawColor(200);
    doc.line(14, 22, 196, 22);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    // Add interpretation text with better formatting
    const interpretationText = [
      'The Modified Checklist for Autism in Toddlers, Revised with Follow-Up (M-CHAT-R/F) is a 2-stage parent-report screening tool to assess the risk of Autism Spectrum Disorder (ASD) in children aged 16 to 30 months.',
      '',
      { text: 'Your Assessment Results:', style: 'subheader' },
      { 
        text: `• ${riskLevel}`,
        style: riskLevel === 'High Risk' ? 'highRisk' : riskLevel === 'Medium Risk' ? 'mediumRisk' : 'lowRisk'
      },
      { text: `   ${getRiskInterpretation(riskLevel)}`, margin: [0, 0, 0, 10] },
      '',
      { text: 'Important Notes:', style: 'subheader' },
      '• This screening tool is not a diagnostic instrument and should not be used as the sole basis for diagnosis.',
      '• A positive screening result indicates the need for a more comprehensive evaluation by a qualified professional.',
      '• A negative screening result does not rule out the need for further evaluation if developmental concerns exist.',
      '',
      { text: 'Recommended Next Steps:', style: 'subheader' },
      '1. Share these results with your pediatrician or healthcare provider.',
      '2. Consider a comprehensive developmental evaluation if concerns persist.',
      '3. Contact early intervention services in your area for further assessment if needed.'
    ];
    
    // Add styled text
    interpretationText.forEach((item, index) => {
      if (typeof item === 'string') {
        const splitText = doc.splitTextToSize(item, 180);
        doc.text(splitText, 20, 40 + (index * 5));
      } else if (item.style === 'subheader') {
        doc.setFont('helvetica', 'bold');
        doc.text(item.text, 20, 40 + (index * 5));
        doc.setFont('helvetica', 'normal');
      } else if (item.style === 'highRisk') {
        doc.setFillColor(248, 215, 218);
        doc.rect(20, 35 + (index * 5), 170, 6, 'F');
        doc.text(item.text, 20, 40 + (index * 5));
      } else if (item.style === 'mediumRisk') {
        doc.setFillColor(255, 243, 205);
        doc.rect(20, 35 + (index * 5), 170, 6, 'F');
        doc.text(item.text, 20, 40 + (index * 5));
      } else if (item.style === 'lowRisk') {
        doc.setFillColor(212, 237, 218);
        doc.rect(20, 35 + (index * 5), 170, 6, 'F');
        doc.text(item.text, 20, 40 + (index * 5));
      }
    });
    
    // Add professional disclaimer
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text('This report was generated by the ASD Flow platform and is intended for informational purposes only. It does not constitute medical advice, diagnosis, or treatment.', 14, 250, { maxWidth: 182, align: 'justify' });
    
    // Add footer with page numbers and confidentiality notice
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Add page number
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
      
      // Add confidentiality notice
      doc.setFontSize(7);
      doc.text('CONFIDENTIAL - This document contains sensitive information and is intended solely for the use of the individual or entity to whom it is addressed.', 105, 290, { align: 'center', maxWidth: 180 });
      
      // Add small footer with logo and website
      doc.setFontSize(6);
      doc.setTextColor(100);
      doc.text('ASD Flow - Empowering Autism Care', 14, 290);
      doc.text('www.asdflow.com', 196, 290, { align: 'right' });
      
      // Add decorative line
      doc.setDrawColor(200);
      doc.line(14, 282, 196, 282);
    }
    
    return doc;
  };
  
  const getRiskInterpretation = (riskLevel) => {
    switch(riskLevel) {
      case 'High Risk':
        return 'The child has screened positive. The child should receive a diagnostic evaluation and early intervention/early childhood services as soon as possible.';
      case 'Medium Risk':
        return 'The child should have a follow-up interview and rescreen in the future if concerns persist.';
      case 'Low Risk':
        return 'The child is at low risk for ASD. No further action is required unless surveillance indicates risk for ASD.';
      default:
        return 'Unable to determine risk level.';
    }
  };

  const handleDownloadPdf = () => {
    if (!assessmentData) return;
    
    // Check if user is not logged in and this is a temporary user
    if (!user && !isTempUser) {
      // Render the component
      return (
        <div className="text-center py-12">
          <p className="text-lg text-gray-700">Please sign in to download your assessment report.</p>
          <button
            onClick={() => navigate('/auth', {
              state: {
                redirectTo: `/app/report/${assessmentId}`,
                message: 'Please sign in to download your assessment report',
                assessmentData: isTempUser ? assessmentData : null
              }
            })}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FaArrowLeft className="-ml-1 mr-2 h-5 w-5" />
            Sign in
          </button>
        </div>
      );
    }
    
    // Generate and download the PDF
    const pdfDoc = generateReportContent();
    if (pdfDoc) {
      pdfDoc.save(`M-CHAT-Report-${assessmentData.childName || 'Assessment'}.pdf`);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p>Loading assessment report...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => navigate('/app/assessments')}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                ← Back to Assessments
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!assessmentData) {
      return (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No assessment data</h3>
          <p className="mt-1 text-sm text-gray-500">We couldn't find the assessment you're looking for.</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => navigate('/app/assessments')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaArrowLeft className="-ml-1 mr-2 h-5 w-5" />
              Back to Assessments
            </button>
          </div>
        </div>
      );
    }

    // Main report content
    if (isPaymentRequired()) {
      return (
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <FaLock className="h-6 w-6 text-yellow-600" aria-hidden="true" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 sm:text-4xl">Unlock Your Full Report</h2>
          <p className="mt-4 text-lg text-gray-500">
            Get detailed insights, risk analysis, and actionable next steps by unlocking the full assessment report.
          </p>
          <div className="my-8">
            <p className="text-indigo-600 font-semibold mb-2">Limited Time Offer!</p>
            <div>
              <span className="text-5xl font-extrabold text-gray-900">₹99</span>
              <span className="text-xl text-gray-500 line-through ml-2">₹999</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/payment', { 
              state: { 
                assessmentId, 
                redirectTo: window.location.pathname 
              } 
            })}
            className="mt-4 w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:w-auto"
          >
            Unlock Now for ₹99
          </button>
          <button
            onClick={() => navigate('/app/assessments')}
            className="mt-4 w-full inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:ml-4"
          >
            <FaArrowLeft className="-ml-1 mr-2 h-5 w-5" />
            Back to Assessments
          </button>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">M-CHAT-R/F Assessment Report</h1>
            <div className="flex items-center space-x-3">
                <button
                  onClick={handleDownloadPdf}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <FaDownload className="mr-2" />
                  Download PDF
                </button>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Child Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">Child's Name</p>
                <p className="font-medium">{assessmentData.childName || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Age</p>
                <p className="font-medium">
                  {assessmentData.childsAgeInMonths || 'Not provided'} months
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Gender</p>
                <p className="font-medium">{assessmentData.childGender || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Assessment Date</p>
                <p className="font-medium">
                  {assessmentData.createdAt?.toDate 
                    ? new Date(assessmentData.createdAt.toDate()).toLocaleDateString() 
                    : new Date(assessmentData.createdAt || new Date()).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Results Summary</h3>
              <div className="flex items-center">
                <div className="flex-shrink-0 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-800">
                    Risk Level: <span className="font-bold">{calculateRiskLevel(assessmentData)}</span>
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    {getRiskInterpretation(calculateRiskLevel(assessmentData))}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">Detailed Responses</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Flagged</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {MCHAT_QUESTIONS.map((question, index) => {
                      const answer = assessmentData.answers && assessmentData.answers[question.id] !== undefined 
                        ? assessmentData.answers[question.id] === 'yes' ? 'Yes' : 'No' 
                        : 'Not answered';
                      const isRiskAnswer = answer.toLowerCase() === question.risk_answer;
                      
                      return (
                        <tr key={question.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-normal text-sm text-gray-900 max-w-md">
                            <span className="font-medium">Q{question.id}.</span> {question.text}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {answer}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                isRiskAnswer ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {isRiskAnswer ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Important:</strong> This is not a diagnosis. A positive screening result should be followed up with a comprehensive evaluation by a qualified professional.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => navigate('/app/assessments')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaArrowLeft className="mr-2" />
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Main component render
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
};

export default AssessmentReport;
